import { XIcon } from "lucide-react";
import { forwardRef, useEffect } from "react";
import { cn } from "@eaigle/utils/cn";
import {
  useDebouncedValue,
  useWorkerSearch,
  type SearchableRecord,
  type SearchTokenize,
} from "./hooks";

export interface SearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  inputClassName?: string;
  /** Accessible label for the search input. */
  "aria-label"?: string;
  /** Accessible label for the clear button. */
  clearButtonLabel?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  onClear?: () => void;
  /**
   * When provided, runs a client-side fuzzy/full-text search (FlexSearch in a dedicated Web
   * Worker, via {@link import("./hooks/useWorkerSearch").useWorkerSearch}) over these
   * `{ id, text }` records every time `value` changes, and reports matches via
   * `onMatchedIdsChange` - so a list can be made searchable just by passing its rows here
   * instead of wiring the worker hook by hand. Debounced internally (see `searchDebounceMs`).
   * Pass a memoized array (identity change re-indexes).
   */
  records?: SearchableRecord[];
  /**
   * Called with the matched record ids whenever `records`-based search completes; `null` means
   * "no query" (nothing filtered - show everything). Only fires when `records` is provided.
   */
  onMatchedIdsChange?: (matchedIds: Set<string> | null) => void;
  /** Debounce (ms) before searching `records`. Default 300. Ignored when `records` isn't provided. */
  searchDebounceMs?: number;
  /**
   * FlexSearch partial-match mode for `records` search (see {@link SearchTokenize}). Defaults to
   * "forward" (matches word-prefixes, e.g. "min" matches "Minutes") - the sane choice for
   * human-readable label text. Pass "full" only for opaque-id search where any substring should
   * match (e.g. a truck/trailer number) - it indexes down to single characters, so short or
   * repetitive queries over label text can match almost anything.
   */
  recordsTokenize?: SearchTokenize;
}

/**
 * Search input with a clear button (shown when value is not empty).
 * Client-side filtering: either pass `records`/`onMatchedIdsChange` to search via the shared
 * worker, or filter the list yourself (e.g. with `fuzzyMatch`) and use `highlightMatchedText`
 * for results.
 */
export const Search = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      value,
      onChange,
      placeholder = "Search…",
      id,
      className = "",
      inputClassName = "",
      "aria-label": ariaLabel = "Search",
      clearButtonLabel = "Clear search",
      disabled = false,
      icon,
      onClear,
      records,
      onMatchedIdsChange,
      searchDebounceMs = 300,
      recordsTokenize = "forward",
    },
    ref,
  ) => {
    const debouncedValue = useDebouncedValue(value, searchDebounceMs);
    const { search, matchedIds } = useWorkerSearch(records ?? null, recordsTokenize);

    useEffect(() => {
      search(debouncedValue);
    }, [debouncedValue, search]);

    useEffect(() => {
      onMatchedIdsChange?.(matchedIds);
    }, [matchedIds, onMatchedIdsChange]);

    return (
      <div className={cn("relative w-full", className)}>
        {icon && (
          <div className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-gray-400">
            {icon}
          </div>
        )}
        <input
          ref={ref}
          id={id}
          type="text"
          autoComplete="off"
          role="searchbox"
          aria-label={ariaLabel}
          className={cn(
            "h-9 w-full rounded-lg border border-gray-300 bg-white py-2 text-sm text-gray-900 placeholder:text-neutral-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/60 disabled:opacity-50",
            icon ? "pl-11 pr-10" : "px-4 pr-10",
            inputClassName,
          )}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => {
              onChange("");
              onClear?.();
            }}
            className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            aria-label={clearButtonLabel}
          >
            <span aria-hidden className="leading-none text-current">
              <XIcon className="size-4" />
            </span>
          </button>
        )}
      </div>
    );
  },
);

Search.displayName = "Search";
